import { ContractStandardField, EntityTemplateField, SystemStandardField } from "../EntityTemplate.types.ts";
import { SystemModel } from "../../system/systemEdit/SystemEdit.types.ts";
import { ContractModel } from "../../contract/contract/Contract.types.ts";
import { SystemCreationFieldSelections, systemCreationFieldIds } from "../../system/systemCreation.ts";
import { applyCustomFieldValue } from "../fields/customFields/customField.hooks.ts";
import { isEmptyFieldValue } from "../../contract/contractAiUpdate/contractFieldValue.ts";

// What an asset create form holds, split the way the create pane keeps it
export interface SystemDraft {
    system: Partial<SystemModel>;
    tagIds: Array<string>;
    creationFieldSelections: SystemCreationFieldSelections;
}

export interface ContractDraft {
    contract: Partial<ContractModel>;
    tagIds: Array<string>;
    // The create form never shows the parties, so their default is only ever the legal entities to add them from
    counterPartyLegalEntityIds: Array<string>;
}

// The field inputs read these lists without checking that they are there
function createEmptySystem(): Partial<SystemModel> {
    return {
        businessAreas: [],
        accessBusinessAreas: [],
        groupEntities: [],
        additionalAccessUsers: [],
        excludedUsers: [],
        customFieldValues: [],
        visibleToAllUsers: false,
        suppliers: [],
        certifications: [],
        dataStorageLocations: [],
        supportAccessLocations: [],
        securityMeasures: [],
        policies: [],
        dataTypes: [],
    };
}

function createEmptyContract(): Partial<ContractModel> {
    return {
        businessAreas: [],
        contractAccessBusinessAreas: [],
        groupEntities: [],
        additionalAccessUsers: [],
        excludedUsers: [],
        counterParties: [],
        customFieldValues: [],
        visibleToAllUsers: false,
    };
}

const systemModelProperties: Partial<Record<SystemStandardField, keyof SystemModel>> = {
    [SystemStandardField.SystemDescription]: "description",
    [SystemStandardField.SystemStatus]: "status",
    [SystemStandardField.DistributionArea]: "distributionArea",
    [SystemStandardField.SystemEntityType]: "entityType",
    [SystemStandardField.SystemStorageType]: "storageType",
    [SystemStandardField.SystemDataTypes]: "dataTypes",
    [SystemStandardField.SystemNetwork]: "network",
    [SystemStandardField.SystemBusinessAreas]: "businessAreas",
    [SystemStandardField.SystemCertifications]: "certifications",
    [SystemStandardField.SystemDataStorageLocations]: "dataStorageLocations",
    [SystemStandardField.SystemSupportAccessLocations]: "supportAccessLocations",
    [SystemStandardField.SystemAdditionalUserAccess]: "additionalAccessUsers",
    [SystemStandardField.SystemBusinessAreaAccess]: "accessBusinessAreas",
    [SystemStandardField.SystemVisibleToAllUsers]: "visibleToAllUsers",
};

// One input answers several properties, so the default holds each of them by name
const systemCompositeProperties: Partial<Record<SystemStandardField, Array<keyof SystemModel>>> = {
    [SystemStandardField.SystemDeletionPeriod]: ["deletionPeriodId", "deletionPeriodReason"],
    [SystemStandardField.SystemIsCriticalForOperation]: ["isCriticalForOperation", "operationCriticalityLevel"],
    [SystemStandardField.SystemUsesAI]: [
        "usesAI",
        "companyUsesAIService",
        "aiUsageDescription",
        "isUseOfAIConsideredHighRisk",
        "isDataUsedForTraningOfAIModel",
    ],
};

const contractModelProperties: Partial<Record<ContractStandardField, keyof ContractModel>> = {
    [ContractStandardField.Description]: "description",
    [ContractStandardField.ReferenceNumber]: "referenceNumber",
    [ContractStandardField.Note]: "note",
    [ContractStandardField.SpecialTerms]: "specialTerms",
    [ContractStandardField.ContractType]: "contractTypeId",
    [ContractStandardField.PaymentTerm]: "paymentTermId",
    [ContractStandardField.Currency]: "currencyCode",
    [ContractStandardField.Status]: "status",
    [ContractStandardField.BusinessAreas]: "businessAreas",
    [ContractStandardField.AdditionalUserAccess]: "additionalAccessUsers",
    [ContractStandardField.BusinessAreaAccess]: "contractAccessBusinessAreas",
    [ContractStandardField.VisibleToAllUsers]: "visibleToAllUsers",
    [ContractStandardField.LegalEntities]: "allGroupEntityAccess",
    [ContractStandardField.NotificationOfContractExpiry]: "contractExpiryNotificationInDays",
    [ContractStandardField.TotalAmount]: "totalAmount",
    [ContractStandardField.FinalisedAmount]: "finalizedAmount",
};

export function buildSystemDefaults(fields: Array<EntityTemplateField>): SystemDraft {
    return fieldsWithDefault(fields).reduce((draft, field) => withSystemDefault(draft, field, field.defaultValue), {
        system: createEmptySystem(),
        tagIds: [],
        creationFieldSelections: {},
    } as SystemDraft);
}

function withSystemDefault(draft: SystemDraft, field: EntityTemplateField, value: unknown): SystemDraft {
    if (field.customInputFieldId) {
        const system = { ...draft.system };
        applyCustomFieldValue(system, field.customInputFieldId, value);
        return { ...draft, system };
    }

    const standardField = field.standardFieldIdentifier as SystemStandardField;
    if (standardField === SystemStandardField.SystemTags) {
        return { ...draft, tagIds: (value as Array<string>) ?? [] };
    }

    if (systemCreationFieldIds.has(standardField)) {
        return { ...draft, creationFieldSelections: { ...draft.creationFieldSelections, [standardField]: (value as Array<string>) ?? [] } };
    }

    const compositeProperties = systemCompositeProperties[standardField];
    if (compositeProperties) {
        return { ...draft, system: { ...draft.system, ...pick(value as Partial<SystemModel>, compositeProperties) } };
    }

    const property = systemModelProperties[standardField];
    return property ? { ...draft, system: { ...draft.system, [property]: value ?? undefined } } : draft;
}

export function readSystemDefault(draft: SystemDraft, field: EntityTemplateField): unknown {
    if (field.customInputFieldId) {
        return toDefaultValue(draft.system.customFieldValues?.find((value) => value.customInputFieldId === field.customInputFieldId)?.value);
    }

    const standardField = field.standardFieldIdentifier as SystemStandardField;
    if (standardField === SystemStandardField.SystemTags) {
        return toDefaultValue(draft.tagIds);
    }

    if (systemCreationFieldIds.has(standardField)) {
        return toDefaultValue(draft.creationFieldSelections[standardField]);
    }

    const compositeProperties = systemCompositeProperties[standardField];
    if (compositeProperties) {
        return toCompositeDefaultValue(pick(draft.system, compositeProperties));
    }

    const property = systemModelProperties[standardField];
    return property ? toDefaultValue(draft.system[property]) : null;
}

export function buildContractDefaults(fields: Array<EntityTemplateField>): ContractDraft {
    return fieldsWithDefault(fields).reduce((draft, field) => withContractDefault(draft, field, field.defaultValue), {
        contract: createEmptyContract(),
        tagIds: [],
        counterPartyLegalEntityIds: [],
    } as ContractDraft);
}

function withContractDefault(draft: ContractDraft, field: EntityTemplateField, value: unknown): ContractDraft {
    if (field.customInputFieldId) {
        const contract = { ...draft.contract };
        applyCustomFieldValue(contract, field.customInputFieldId, value);
        return { ...draft, contract };
    }

    const standardField = field.standardFieldIdentifier as ContractStandardField;
    if (standardField === ContractStandardField.Tags) {
        return { ...draft, tagIds: (value as Array<string>) ?? [] };
    }

    if (standardField === ContractStandardField.CounterParties) {
        return { ...draft, counterPartyLegalEntityIds: (value as Array<string>) ?? [] };
    }

    const property = contractModelProperties[standardField];
    return property ? { ...draft, contract: { ...draft.contract, [property]: value ?? undefined } } : draft;
}

export function readContractDefault(draft: ContractDraft, field: EntityTemplateField): unknown {
    if (field.customInputFieldId) {
        return toDefaultValue(draft.contract.customFieldValues?.find((value) => value.customInputFieldId === field.customInputFieldId)?.value);
    }

    const standardField = field.standardFieldIdentifier as ContractStandardField;
    if (standardField === ContractStandardField.Tags) {
        return toDefaultValue(draft.tagIds);
    }

    if (standardField === ContractStandardField.CounterParties) {
        return toDefaultValue(draft.counterPartyLegalEntityIds);
    }

    const property = contractModelProperties[standardField];
    return property ? toDefaultValue(draft.contract[property]) : null;
}

// A default stands in for the user's own answer, so like an answer it only reaches a field they may edit. Tags are not
// governed by the field permissions but by a permission of their own
export function fieldsTheUserMayEdit(fields: Array<EntityTemplateField>, hasTagsAccess: boolean): Array<EntityTemplateField> {
    return fields.filter((field) => !field.disabled && (hasTagsAccess || !isTagsField(field)));
}

function isTagsField(field: EntityTemplateField) {
    return field.standardFieldIdentifier === ContractStandardField.Tags || field.standardFieldIdentifier === SystemStandardField.SystemTags;
}

export function isGroupCompaniesField(field: EntityTemplateField) {
    return field.standardFieldIdentifier === ContractStandardField.LegalEntities || field.standardFieldIdentifier === SystemStandardField.SystemGroupCompanies;
}

function fieldsWithDefault(fields: Array<EntityTemplateField>) {
    return fields.filter((field) => field.defaultValue !== undefined && field.defaultValue !== null);
}

function pick<T>(source: Partial<T>, properties: Array<keyof T>): Partial<T> {
    return Object.fromEntries(properties.filter((property) => source[property] !== undefined).map((property) => [property, source[property]])) as Partial<T>;
}

// An emptied field clears the default rather than storing an empty value. An amount field keeps its currency when
// the amount is cleared, so it counts as empty without an amount
function toDefaultValue(value: unknown): unknown {
    if (isEmptyFieldValue(value) || (isAmount(value) && isEmptyFieldValue(value.amount))) {
        return null;
    }

    return value;
}

function isAmount(value: unknown): value is { amount: unknown } {
    return typeof value === "object" && value !== null && "amount" in value;
}

function toCompositeDefaultValue(value: object): unknown {
    return Object.values(value).every((part) => isEmptyFieldValue(part)) ? null : value;
}
